/**
 * Sync de mensagens da conversa ativa: fetch, cache, overrides e realtime.
 */
import { useCallback, useEffect, useState, useRef } from 'react';
import { ChatMessage } from './types';
import { createModuleLogger } from '../../../utils/logger';
import { isAbortError } from '../../../utils/networkError';
import { isValidJid } from '../../../utils/phoneUtils';
import { useChatMetadata } from './useChatMetadata';
import { useChatRealtime } from './useChatRealtime';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';
import { useChatPagination } from './useChatPagination';
import { useChatOptimistic } from './useChatOptimistic';
import {
    applyMessageOverrides,
    applyProviderTimestampToMessages,
    dedupeMessages,
    fetchConversationMessages,
    hintKey,
    mergeFetchedWithDisplay,
    mergePatch,
    messagesHash,
    parseProviderTimestamp,
    upsertRealtimeMessage,
} from './chatSync.helpers';
import { useChatSyncLifecycle } from './useChatSync.lifecycle';
import { usePushActiveConversation } from '../../../hooks/usePushActiveConversation';
import type { PartyMessagePayload } from './usePartyRealtime';
import {
    PARTYKIT_FETCH_THROTTLE_MS,
    PARTYKIT_MESSAGE_REFRESH_MS,
} from '../utils/partykitSocket';

const log = createModuleLogger('[ChatSync]');

export function useChatSync(
    instanceName: string | undefined,
    activeConversationId: string | null,
    connectionId?: string | null,
) {
    const { currentOrganization } = useOrganization();
    const { currentPage } = useAppContext();
    const isChatActive = currentPage === 'chat';
    const syncKey = activeConversationId ? `${currentOrganization?.id}:${connectionId || instanceName}:${activeConversationId}` : null;
    const { conversationDbId, setConversationDbId, userId } = useChatMetadata(activeConversationId, connectionId);
    usePushActiveConversation(conversationDbId);

    const [currentMessages, setCurrentMessages] = useState<ChatMessage[]>([]);
    const [isFetchingMessages, setIsFetchingMessages] = useState(false);

    const { loadOlderMessages: baseLoadOlderMessages, isLoadingOlder, hasMoreMessages, resetPagination } =
        useChatPagination(conversationDbId, currentOrganization?.id);
    const {
        addOptimisticMessage, replaceOptimisticMessage, removeOptimisticMessage,
        getRemainingOptimisticMessages, getPendingOptimistic,
    } = useChatOptimistic(syncKey, setCurrentMessages);

    const activeIdRef = useRef(syncKey);
    const dbIdRef = useRef(conversationDbId);
    const orgIdRef = useRef(currentOrganization?.id);
    const connIdRef = useRef(connectionId);
    const fetchBusyRef = useRef<{ key: string; controller: AbortController } | null>(null);
    const pendingRefetchRef = useRef(false);
    const lastFetchDoneRef = useRef(0);
    const lastRealConvRef = useRef<string | null>(null);
    const cacheRef = useRef<Record<string, ChatMessage[]>>({});
    const byIdOverridesRef = useRef(new Map<string, Partial<ChatMessage>>());
    const byHintOverridesRef = useRef(new Map<string, Partial<ChatMessage>>());
    const pendingOptRef = useRef(getPendingOptimistic);
    pendingOptRef.current = getPendingOptimistic;
    const fetchRef = useRef<() => Promise<void>>(async () => {});
    const refreshRef = useRef<() => void>(() => {});
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Ativa: merge no estado React (paginação). Inativa: só cache — evita poluir a conversa aberta. */
    const writeConvMessages = useCallback((convId: string, build: (prev: ChatMessage[]) => ChatMessage[]) => {
        if (activeIdRef.current === convId) {
            setCurrentMessages((current) => {
                const next = build(current);
                cacheRef.current[convId] = next;
                return messagesHash(current) !== messagesHash(next) ? next : current;
            });
            return;
        }
        const prev = cacheRef.current[convId] ?? [];
        cacheRef.current[convId] = build(prev);
    }, []);

    const onDatabaseMessage = useCallback((message: ChatMessage) => {
        const key = activeIdRef.current;
        if (key) writeConvMessages(key, (prev) => upsertRealtimeMessage(prev, message));
    }, [writeConvMessages]);

    // Preview truncado do PartyKit não substitui o corpo; refresh busca o banco.
    // Outbound: não aplica horário do Party (evita piscada) — ordem/relógio vêm do fetch.
    // Inbound: horário do provedor pode reordenar.
    const applyRealtimePayload = useCallback((payload?: PartyMessagePayload) => {
        const key = activeIdRef.current;
        const providerTs = parseProviderTimestamp(payload?.messageTimestamp);
        if (key && payload?.messageId && providerTs) {
            writeConvMessages(key, (prev) => {
                const target = prev.find((m) => m.id === payload.messageId);
                if (target?.sender === 'me') return prev;
                return applyProviderTimestampToMessages(prev, payload.messageId!, providerTs);
            });
        }
        refreshRef.current();
    }, [writeConvMessages]);

    const { isRealtimeConnected, isPeerTyping, sendTyping } = useChatRealtime(
        conversationDbId, applyRealtimePayload, { enabled: isChatActive },
    );
    dbIdRef.current = conversationDbId;
    orgIdRef.current = currentOrganization?.id;
    connIdRef.current = connectionId;

    const applyMessagesToState = useCallback((fetchConvId: string, dbMessages: ChatMessage[]) => {
        const map = new Map(dbMessages.map((m) => [m.id, m] as const));
        getRemainingOptimisticMessages(fetchConvId, map);
        const fromDb = dedupeMessages([...map.values()]);
        writeConvMessages(fetchConvId, (prev) => applyMessageOverrides(
            mergeFetchedWithDisplay(prev, fromDb, pendingOptRef.current(fetchConvId)),
            byIdOverridesRef.current,
            byHintOverridesRef.current,
        ));
    }, [getRemainingOptimisticMessages, writeConvMessages]);

    const patchLocalMessages = useCallback((ids: string[], patch: Partial<ChatMessage>, contentHint?: string) => {
        if (!syncKey || activeIdRef.current !== syncKey) return;
        const hint = contentHint ? hintKey(contentHint) : '';
        for (const id of ids) if (id) mergePatch(byIdOverridesRef.current, id, patch);
        if (hint) mergePatch(byHintOverridesRef.current, hint, patch);
        writeConvMessages(syncKey, (prev) => {
            const mapped = prev.map((m) => {
                const matchHint = Boolean(hint && m.sender === 'me' && m.text && hintKey(m.text) === hint);
                if (!ids.includes(m.id) && !matchHint) return m;
                if (matchHint) mergePatch(byIdOverridesRef.current, m.id, patch);
                return { ...m, ...patch };
            });
            return patch.timestamp ? dedupeMessages(mapped) : mapped;
        });
    }, [syncKey, writeConvMessages]);

    const clearLocalOverride = useCallback((ids: string[], contentHint?: string) => {
        if (activeIdRef.current !== syncKey) return;
        for (const id of ids) if (id) byIdOverridesRef.current.delete(id);
        if (contentHint) byHintOverridesRef.current.delete(hintKey(contentHint));
    }, [syncKey]);

    const queueFetch = useCallback((delayMs = 0) => setTimeout(() => { void fetchRef.current(); }, delayMs), []);
    const scheduleRefresh = useCallback(() => {
        if (refreshTimerRef.current) return;
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            queueFetch();
        }, PARTYKIT_MESSAGE_REFRESH_MS);
    }, [queueFetch]);
    refreshRef.current = scheduleRefresh;

    // Cloud/Instagram usam connectionId; Uazapi também fornece instanceName.
    const canSyncMessages = Boolean(instanceName || connectionId);

    const fetchMsgs = useCallback(async () => {
        if (!isChatActive || !canSyncMessages || !activeConversationId || !isValidJid(activeConversationId) || syncKey !== activeIdRef.current) {
            setIsFetchingMessages(false);
            return;
        }
        if (fetchBusyRef.current?.key === syncKey) {
            pendingRefetchRef.current = true;
            return;
        }

        const since = Date.now() - lastFetchDoneRef.current;
        const cached = cacheRef.current[syncKey!];
        if (since < PARTYKIT_FETCH_THROTTLE_MS && cached?.length) {
            pendingRefetchRef.current = true;
            if (!refreshTimerRef.current) refreshTimerRef.current = setTimeout(() => {
                refreshTimerRef.current = null;
                pendingRefetchRef.current = false;
                void fetchRef.current();
            }, PARTYKIT_FETCH_THROTTLE_MS - since);
            return;
        }

        fetchBusyRef.current?.controller.abort();
        const request = { key: syncKey!, controller: new AbortController() };
        fetchBusyRef.current = request;
        const fetchConvId = activeConversationId;
        try {
            const dbMessages = await fetchConversationMessages(
                fetchConvId, dbIdRef.current, orgIdRef.current, connIdRef.current, request.controller.signal,
            );
            if (!request.controller.signal.aborted && activeIdRef.current === request.key) applyMessagesToState(request.key, dbMessages);
        } catch (error) {
            if (request.controller.signal.aborted || isAbortError(error)) return;
            log.error('Erro ao buscar mensagens:', error);
        } finally {
            if (fetchBusyRef.current === request) {
                fetchBusyRef.current = null;
                if (activeIdRef.current === request.key) {
                    lastFetchDoneRef.current = Date.now();
                    setIsFetchingMessages(false);
                    if (pendingRefetchRef.current) {
                        pendingRefetchRef.current = false;
                        scheduleRefresh();
                    }
                }
            }
        }
    }, [isChatActive, canSyncMessages, activeConversationId, syncKey, conversationDbId, applyMessagesToState, scheduleRefresh]);
    fetchRef.current = fetchMsgs;

    useEffect(() => () => {
        fetchBusyRef.current?.controller.abort();
        fetchBusyRef.current = null;
        byIdOverridesRef.current.clear();
        byHintOverridesRef.current.clear();
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
        pendingRefetchRef.current = false;
    }, [syncKey, isChatActive]);

    useChatSyncLifecycle({
        isChatActive, activeConversationId: syncKey, conversationDbId, connectionId, isRealtimeConnected,
        setCurrentMessages, setIsFetchingMessages, resetPagination, fetchMsgs, scheduleRefresh, onDatabaseMessage,
        activeIdRef, lastRealConvRef, cacheRef, pendingRefetchRef, lastFetchDoneRef,
    });

    const loadOlderMessages = useCallback(
        () => baseLoadOlderMessages(currentMessages, (updater) => {
            setCurrentMessages((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                const key = activeIdRef.current;
                if (key) cacheRef.current[key] = next;
                return next;
            });
        }),
        [baseLoadOlderMessages, currentMessages],
    );

    return {
        messages: lastRealConvRef.current === syncKey ? currentMessages : cacheRef.current[syncKey || ''] || [],
        isLoading: isFetchingMessages && currentMessages.length === 0,
        conversationDbId, setConversationDbId, userId,
        refreshMessages: fetchMsgs,
        addOptimisticMessage, replaceOptimisticMessage, removeOptimisticMessage,
        patchLocalMessages, clearLocalOverride,
        isPeerTyping, sendTyping,
        loadOlderMessages, isLoadingOlder, hasMoreMessages,
    };
}
